import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getSido, getSigungu, getDong,
  queryRegion, queryMaster,
  addRegionRow, regionExists,
  updateRegionCell, updateMasterCell,
  designateSpeculation, designateRegulation,
  getSpeculationList, getRegulationList,
} from '../../lib/api/admin-geupji-api';
import './GeupjiManagement.css';

// 지역데이터 그리드 컬럼 정의 (editable=true 만 백엔드 저장 대상)
const REGION_COLUMNS = [
  { key: 'speculation_zone', label: '투기과열', editable: true, w: 90 },
  { key: 'sido_name', label: '시도', editable: false, w: 90 },
  { key: 'sigungu_name', label: '시군구', editable: false, w: 110 },
  { key: 'bjd_name', label: '읍면동', editable: false, w: 100 },
  { key: 'sigungu_grade', label: '지급', editable: true, w: 60 },
  { key: 'dong_grade', label: '동급', editable: true, w: 60 },
  { key: 'regulation', label: '규제', editable: true, w: 80 },
  { key: 'regulation_date', label: '규제일', editable: true, w: 110 },
  { key: 'preferred_order', label: '선호순위', editable: true, w: 80 },
  // 가격
  { key: 'high_price', label: '최고가', editable: true, w: 100 },
  { key: 'low_price', label: '최저가', editable: true, w: 100 },
  { key: 'avg_price', label: '평균가', editable: true, w: 100 },
  { key: 'sale_price', label: '매매가', editable: true, w: 100 },
  { key: 'lease_price', label: '전세가', editable: true, w: 100 },
  { key: 'sale_volume', label: '매매량', editable: true, w: 80 },
  { key: 'lease_volume', label: '전세량', editable: true, w: 80 },
  // 계산 (읽기전용)
  { key: 'calc_price', label: '계산', editable: false, w: 100, calc: true },
  { key: 'calc_gap', label: '최-현', editable: false, w: 100, calc: true },
  { key: 'highest_level_calc', label: '최고급', editable: false, w: 70, calc: true },
  { key: 'current_level_calc', label: '현재급', editable: false, w: 70, calc: true },
  // 인구
  { key: 'population_grade', label: '인구급', editable: true, w: 70 },
  { key: 'population', label: '인구', editable: true, w: 90 },
  { key: 'households', label: '세대수', editable: true, w: 90 },
  { key: 'move_in_out', label: '전입전출', editable: true, w: 90 },
  // 직업
  { key: 'job_grade', label: '직업급', editable: true, w: 70 },
  { key: 'workers', label: '종사자', editable: true, w: 90 },
  { key: 'business_count', label: '사업체', editable: true, w: 90 },
  { key: 'company_500', label: '500대기업', editable: true, w: 90 },
  // 교통
  { key: 'traffic_grade', label: '교통급', editable: true, w: 70 },
  { key: 'to_gangnam', label: '강남까지', editable: true, w: 80 },
  { key: 'to_major', label: '주요지', editable: true, w: 80 },
  { key: 'to_subway', label: '지하철', editable: true, w: 80 },
  // 학군
  { key: 'school_grade', label: '학군급', editable: true, w: 70 },
  { key: 'school_95', label: '95이상', editable: true, w: 70 },
  { key: 'school_90', label: '90이상', editable: true, w: 70 },
  { key: 'school_85', label: '85이상', editable: true, w: 70 },
  { key: 'school_below', label: '85미만', editable: true, w: 70 },
  { key: 'middle_school', label: '중학교', editable: true, w: 70 },
  { key: 'academy', label: '학원', editable: true, w: 70 },
  // 환경
  { key: 'env_grade', label: '환경급', editable: true, w: 70 },
  { key: 'dept_store', label: '백화점', editable: true, w: 70 },
  { key: 'big_mart', label: '대형마트', editable: true, w: 70 },
  { key: 'env_etc', label: '환경기타', editable: true, w: 120 },
  // 공급
  { key: 'supply_3y_rate', label: '3년공급률', editable: true, w: 90 },
  { key: 'supply_3y_demand', label: '3년수요', editable: true, w: 90 },
  { key: 'supply_3y_movein', label: '3년입주', editable: true, w: 90 },
  { key: 'supply_2026', label: '2026', editable: true, w: 80 },
  { key: 'supply_2027', label: '2027', editable: true, w: 80 },
  { key: 'supply_2028', label: '2028', editable: true, w: 80 },
  // 호재
  { key: 'hojae_type', label: '호재유형', editable: true, w: 100 },
  { key: 'hojae_detail', label: '호재상세', editable: true, w: 140 },
  { key: 'hojae', label: '호재', editable: true, w: 120 },
];

const fmtCell = (v) => {
  if (v === null || v === undefined || v === '') return '';
  return String(v);
};

// 지정 목록을 시도 > 시군구(구 단위) 트리로 표시. 수정/삭제는 구 전체 일괄.
function DesignTree({ rows, renderValue, renderEdit, editKey, onEdit, onSave, onCancel, onDelete, busy }) {
  const [openSido, setOpenSido] = useState({});
  const tree = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => {
      const sido = r.sido_name || '(기타)';
      const sig = r.sigungu_name || '(전체)';
      if (!m.has(sido)) m.set(sido, new Map());
      const sm = m.get(sido);
      if (!sm.has(sig)) sm.set(sig, { count: 0, guCode: `${String(r.bjd_code || '').slice(0, 5)}00000`, sample: r });
      sm.get(sig).count += 1;
    });
    return m;
  }, [rows]);

  if (!rows || rows.length === 0) return <div className="geupji-empty">지정된 지역이 없습니다.</div>;

  return (
    <div className="geupji-tree">
      {[...tree.entries()].map(([sido, sm]) => {
        const so = !!openSido[sido];
        return (
          <div key={sido} className="geupji-tree-sido">
            <button type="button" className="geupji-tree-row lv1" onClick={() => setOpenSido((p) => ({ ...p, [sido]: !p[sido] }))}>
              <span className="tw">{so ? '▾' : '▸'}</span><span className="nm">{sido}</span><span className="cnt">{sm.size}</span>
            </button>
            {so && [...sm.entries()].map(([sig, info]) => {
              const editing = editKey === info.guCode;
              return (
                <div key={info.guCode} className="geupji-tree-leaf lv2">
                  <span className="nm">{sig}</span>
                  <span className="val">{editing ? renderEdit() : renderValue(info.sample)}</span>
                  <span className="act">
                    {editing ? (
                      <>
                        <button type="button" className="geupji-btn xs primary" onClick={() => onSave(info.guCode)} disabled={busy}>저장</button>
                        <button type="button" className="geupji-btn xs" onClick={onCancel} disabled={busy}>취소</button>
                      </>
                    ) : (
                      <>
                        <button type="button" className="geupji-btn xs" onClick={() => onEdit(info.guCode, info.sample)}>수정</button>
                        <button type="button" className="geupji-btn xs danger" onClick={() => onDelete(info.guCode)}>삭제</button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// 우측: 적용된 지역을 시군구 단위로 요약
function DesignSummary({ rows }) {
  const groups = useMemo(() => {
    const m = new Map();
    (rows || []).forEach((r) => {
      const k = [r.sido_name, r.sigungu_name].filter(Boolean).join(' ') || '(기타)';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => String(a[0]).localeCompare(String(b[0]), 'ko'));
  }, [rows]);
  return (
    <div className="geupji-summary">
      <div className="geupji-summary-head">적용 시군구 ({groups.length})</div>
      <div className="geupji-summary-list">
        {groups.length === 0 && <div className="geupji-empty">없음</div>}
        {groups.map(([name, cnt]) => (
          <div key={name} className="geupji-summary-item"><span>{name}</span><b>{cnt}</b></div>
        ))}
      </div>
    </div>
  );
}

function RegionDataPane({ token, sidoList, setError }) {
  const [sigunguList, setSigunguList] = useState([]);
  const [dongList, setDongList] = useState([]);
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [dong, setDong] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // 검색조건: 투기과열/조정 보기 체크박스
  const [specFilter, setSpecFilter] = useState(false);
  const [regFilter, setRegFilter] = useState(false);
  // 트리 접힘 상태: 시도는 기본 펼침(닫힌 것만 보관), 시군구(구)는 기본 접힘(펼친 것만 보관)
  const [closedSido, setClosedSido] = useState(() => new Set());
  const [openSig, setOpenSig] = useState(() => new Set());
  const toggleSido = (k) => setClosedSido((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleSig = (k) => setOpenSig((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  // 지정 관리 모달
  const [specModal, setSpecModal] = useState(false);
  const [regModal, setRegModal] = useState(false);
  const [specValue, setSpecValue] = useState('투기과열');   // 추가 시 값
  const [regReg, setRegReg] = useState('조정');             // 추가 시 규제
  const [regDate, setRegDate] = useState('');               // 추가 시 규제일
  const [busy, setBusy] = useState(false);
  // 지정 목록 + 인라인 편집
  const [specList, setSpecList] = useState([]);
  const [regList, setRegList] = useState([]);
  const [editKey, setEditKey] = useState(null);   // 편집 중 bjd_code
  const [editVal, setEditVal] = useState('');      // 투기과열 편집 값
  const [editReg, setEditReg] = useState('조정');  // 규제 편집 값
  const [editDate, setEditDate] = useState('');    // 규제일 편집 값

  const loadSpecList = useCallback(async () => {
    try { setSpecList(await getSpeculationList(token)); }
    catch (e) { setError(e?.message || '투기과열 목록 조회 실패'); }
  }, [token, setError]);
  const loadRegList = useCallback(async () => {
    try { setRegList(await getRegulationList(token)); }
    catch (e) { setError(e?.message || '규제 목록 조회 실패'); }
  }, [token, setError]);

  // 컬럼 너비(드래그 조절)
  const [colWidths, setColWidths] = useState(() => Object.fromEntries(REGION_COLUMNS.map((c) => [c.key, c.w])));
  const resizingRef = useRef(null);
  const startResize = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    resizingRef.current = { key, startX: e.clientX, startW: colWidths[key] || 80 };
    const onMove = (ev) => {
      const r = resizingRef.current;
      if (!r) return;
      const next = Math.max(40, r.startW + (ev.clientX - r.startX));
      setColWidths((prev) => ({ ...prev, [r.key]: next }));
    };
    const onUp = () => {
      resizingRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // 선택된 가장 깊은 지역코드
  const selectedBjd = dong || sigungu || sido || '';
  const selectedRegionLabel = useMemo(() => {
    const s = sidoList.find((o) => o.code === sido)?.label || '';
    const g = sigunguList.find((o) => o.code === sigungu)?.label || '';
    const d = dongList.find((o) => o.code === dong)?.label || '';
    return [s, g, d].filter(Boolean).join(' ') || '(지역 미선택)';
  }, [sidoList, sigunguList, dongList, sido, sigungu, dong]);

  const onSelectSido = async (code) => {
    setSido(code); setSigungu(''); setDong(''); setSigunguList([]); setDongList([]);
    if (!code) return;
    try { setSigunguList(await getSigungu(token, code)); }
    catch (e) { setError(e?.message || '시군구 조회 실패'); }
  };
  const onSelectSigungu = async (code) => {
    setSigungu(code); setDong(''); setDongList([]);
    if (!code) return;
    try { setDongList(await getDong(token, code)); }
    catch (e) { setError(e?.message || '읍면동 조회 실패'); }
  };

  const doQuery = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await queryRegion(token, selectedBjd, { speculation: specFilter, regulation: regFilter })); }
    catch (e) { setError(e?.message || '조회 실패'); setRows([]); }
    finally { setLoading(false); }
  }, [token, selectedBjd, specFilter, regFilter, setError]);

  // 조회 결과를 시도 > 시군구 > 읍면동(행) 트리로 그룹화 (백엔드 정렬 순서 유지, 원본 인덱스 보존)
  const regionTree = useMemo(() => {
    const sidos = [];
    const sidoMap = new Map();
    (rows || []).forEach((r, idx) => {
      const sido = r.sido_name || '(기타)';
      const sig = r.sigungu_name || '(기타)';
      let sNode = sidoMap.get(sido);
      if (!sNode) { sNode = { sido, sigMap: new Map(), sigs: [], count: 0 }; sidoMap.set(sido, sNode); sidos.push(sNode); }
      sNode.count += 1;
      let gNode = sNode.sigMap.get(sig);
      if (!gNode) { gNode = { sig, rows: [] }; sNode.sigMap.set(sig, gNode); sNode.sigs.push(gNode); }
      gNode.rows.push({ row: r, idx });
    });
    return sidos;
  }, [rows]);

  const onAddRow = async () => {
    if (!selectedBjd) { setError('지역을 선택하세요.'); return; }
    let overwrite = false;
    const exists = await regionExists(token, selectedBjd);
    if (exists.exists) {
      if (!window.confirm('이미 존재하는 지역입니다. 갱신하시겠습니까?')) return;
      overwrite = true;
    }
    const res = await addRegionRow(token, selectedBjd, overwrite);
    if (!res.ok) { setError(res.message || '행 추가 실패'); return; }
    await doQuery();
  };

  const openSpecModal = async () => { setEditKey(null); setSpecValue('투기과열'); setSpecModal(true); await loadSpecList(); };
  const openRegModal = async () => { setEditKey(null); setRegReg('조정'); setRegDate(''); setRegModal(true); await loadRegList(); };

  // 추가 (상단에서 선택한 지역의 하위 일괄)
  const addSpeculation = async () => {
    if (!selectedBjd) { setError('추가하려면 상단에서 지역을 선택하세요.'); return; }
    setBusy(true); setError('');
    try {
      const res = await designateSpeculation(token, { bjdCode: selectedBjd, value: specValue });
      if (!res.ok) { setError(res.message || '추가 실패'); return; }
      await loadSpecList(); await doQuery();
    } finally { setBusy(false); }
  };
  const addRegulation = async () => {
    if (!selectedBjd) { setError('추가하려면 상단에서 지역을 선택하세요.'); return; }
    setBusy(true); setError('');
    try {
      const res = await designateRegulation(token, { bjdCode: selectedBjd, regulation: regReg, regulationDate: regDate });
      if (!res.ok) { setError(res.message || '추가 실패'); return; }
      await loadRegList(); await doQuery();
    } finally { setBusy(false); }
  };

  // 수정/삭제 (구 단위 일괄 — 해당 시군구 하위 전체)
  const saveSpecGroup = async (guCode) => {
    setBusy(true); setError('');
    try {
      const res = await designateSpeculation(token, { bjdCode: guCode, value: editVal });
      if (!res.ok) { setError(res.message || '수정 실패'); return; }
      setEditKey(null); await loadSpecList(); await doQuery();
    } finally { setBusy(false); }
  };
  const deleteSpecGroup = async (guCode) => {
    if (!window.confirm('이 시군구 전체의 투기과열 지정을 해제하시겠습니까?')) return;
    setBusy(true); setError('');
    try {
      const res = await designateSpeculation(token, { bjdCode: guCode, value: '' });
      if (!res.ok) { setError(res.message || '삭제 실패'); return; }
      setEditKey(null); await loadSpecList(); await doQuery();
    } finally { setBusy(false); }
  };
  const saveRegGroup = async (guCode) => {
    setBusy(true); setError('');
    try {
      const res = await designateRegulation(token, { bjdCode: guCode, regulation: editReg, regulationDate: editDate });
      if (!res.ok) { setError(res.message || '수정 실패'); return; }
      setEditKey(null); await loadRegList(); await doQuery();
    } finally { setBusy(false); }
  };
  const deleteRegGroup = async (guCode) => {
    if (!window.confirm('이 시군구 전체의 규제 지정을 해제하시겠습니까?')) return;
    setBusy(true); setError('');
    try {
      const res = await designateRegulation(token, { bjdCode: guCode, regulation: '', regulationDate: '' });
      if (!res.ok) { setError(res.message || '삭제 실패'); return; }
      setEditKey(null); await loadRegList(); await doQuery();
    } finally { setBusy(false); }
  };

  // 셀 저장 (blur/Enter)
  const saveCell = async (rowIdx, col, newValue) => {
    const row = rows[rowIdx];
    if (!row) return;
    const oldValue = row[col.key];
    if (String(oldValue ?? '') === String(newValue ?? '')) return;
    // 낙관적 반영
    setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [col.key]: newValue } : r)));
    const res = await updateRegionCell(token, { bjdCode: row.bjd_code, field: col.key, value: newValue });
    if (!res.ok) {
      setError(res.message || '셀 저장 실패');
      // 롤백
      setRows((prev) => prev.map((r, i) => (i === rowIdx ? { ...r, [col.key]: oldValue } : r)));
    }
  };

  return (
    <div className="geupji-pane">
      <div className="geupji-toolbar">
        <select value={sido} onChange={(e) => onSelectSido(e.target.value)}>
          <option value="">시/도</option>
          {sidoList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <select value={sigungu} onChange={(e) => onSelectSigungu(e.target.value)} disabled={!sido}>
          <option value="">시/군/구</option>
          {sigunguList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <select value={dong} onChange={(e) => setDong(e.target.value)} disabled={!sigungu}>
          <option value="">읍/면/동</option>
          {dongList.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
        </select>
        <label className="geupji-check">
          <input type="checkbox" checked={specFilter} onChange={(e) => setSpecFilter(e.target.checked)} />
          투기과열 보기
        </label>
        <label className="geupji-check">
          <input type="checkbox" checked={regFilter} onChange={(e) => setRegFilter(e.target.checked)} />
          조정지역 보기
        </label>
        <button className="geupji-btn primary" onClick={doQuery} disabled={loading}>조회</button>
        <span className="geupji-spacer" />
        <button className="geupji-btn" onClick={onAddRow}>행추가</button>
        <button className="geupji-btn" onClick={openSpecModal}>투기과열지정</button>
        <button className="geupji-btn" onClick={openRegModal}>규제 지정</button>
      </div>

      <div className="geupji-grid-wrap">
        <table className="geupji-grid resizable">
          <thead>
            <tr>
              {REGION_COLUMNS.map((c) => (
                <th
                  key={c.key}
                  style={{ width: colWidths[c.key], minWidth: colWidths[c.key], maxWidth: colWidths[c.key] }}
                  className={c.calc ? 'calc' : ''}
                >
                  <span className="geupji-th-label">{c.label}</span>
                  <span
                    className="geupji-col-resizer"
                    onMouseDown={(e) => startResize(e, c.key)}
                    role="separator"
                    aria-label={`${c.label} 너비 조절`}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td className="geupji-empty" colSpan={REGION_COLUMNS.length}>{loading ? '조회 중...' : '데이터가 없습니다. 지역 선택 후 [조회]하세요.'}</td></tr>
            )}
            {regionTree.map((sNode) => {
              const sidoOpen = !closedSido.has(sNode.sido);
              return (
                <React.Fragment key={sNode.sido}>
                  <tr className="geupji-tree-group lv-sido">
                    <td colSpan={REGION_COLUMNS.length} style={{ padding: 0, background: '#dbe3ec' }}>
                      <button
                        type="button"
                        className="geupji-tree-toggle"
                        onClick={() => toggleSido(sNode.sido)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', border: 0, background: 'transparent', cursor: 'pointer', padding: '7px 10px', font: 'inherit', fontWeight: 800, color: '#0f172a' }}
                      >
                        <span style={{ width: 12, color: '#475569' }}>{sidoOpen ? '▾' : '▸'}</span>
                        <span>{sNode.sido}</span>
                        <span className="cnt">{sNode.count}</span>
                      </button>
                    </td>
                  </tr>
                  {sidoOpen && sNode.sigs.map((gNode) => {
                    const sigKey = `${sNode.sido}|${gNode.sig}`;
                    const sigOpen = openSig.has(sigKey);
                    return (
                      <React.Fragment key={sigKey}>
                        <tr className="geupji-tree-group lv-sig">
                          <td colSpan={REGION_COLUMNS.length} style={{ padding: 0, background: '#eef2f7' }}>
                            <button
                              type="button"
                              className="geupji-tree-toggle indent"
                              onClick={() => toggleSig(sigKey)}
                              style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', border: 0, background: 'transparent', cursor: 'pointer', padding: '6px 10px 6px 30px', font: 'inherit', fontWeight: 700, color: '#334155' }}
                            >
                              <span style={{ width: 12, color: '#475569' }}>{sigOpen ? '▾' : '▸'}</span>
                              <span>{gNode.sig}</span>
                              <span className="cnt">{gNode.rows.length}</span>
                            </button>
                          </td>
                        </tr>
                        {sigOpen && gNode.rows.map(({ row, idx }) => (
                          <tr key={row.bjd_code || idx} className="geupji-tree-data">
                            {REGION_COLUMNS.map((c) => (
                              <td key={c.key} className={`${c.editable ? 'editable' : ''} ${c.calc ? 'calc' : ''}`.trim()}>
                                {c.editable ? (
                                  <input
                                    defaultValue={fmtCell(row[c.key])}
                                    onBlur={(e) => saveCell(idx, c, e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  />
                                ) : (
                                  fmtCell(row[c.key])
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {specModal && (
        <div className="geupji-modal-backdrop" onClick={() => !busy && setSpecModal(false)}>
          <div className="geupji-modal wide" onClick={(e) => e.stopPropagation()}>
            <div className="geupji-modal-head">투기과열 관리</div>
            <div className="geupji-modal-body">
              <div className="geupji-add-row">
                <span className="geupji-add-region">{selectedRegionLabel}</span>
                <select value={specValue} onChange={(e) => setSpecValue(e.target.value)}>
                  <option value="투기과열">투기과열</option>
                </select>
                <button className="geupji-btn primary" onClick={addSpeculation} disabled={busy}>추가</button>
              </div>
              <div className="geupji-manage-split">
                <div className="geupji-manage-left">
                  <DesignTree
                    rows={specList}
                    busy={busy}
                    editKey={editKey}
                    renderValue={(r) => r.speculation_zone ?? ''}
                    renderEdit={() => <input value={editVal} onChange={(e) => setEditVal(e.target.value)} />}
                    onEdit={(guCode, r) => { setEditKey(guCode); setEditVal(r.speculation_zone ?? ''); }}
                    onSave={(guCode) => saveSpecGroup(guCode)}
                    onCancel={() => setEditKey(null)}
                    onDelete={(guCode) => deleteSpecGroup(guCode)}
                  />
                </div>
                <div className="geupji-manage-right">
                  <DesignSummary rows={specList} />
                </div>
              </div>
              <p className="geupji-modal-note">시도 &gt; 시군구(구 단위)로 표기. 추가·수정·삭제 모두 해당 시군구 하위 전체에 일괄 적용됩니다.</p>
            </div>
            <div className="geupji-modal-actions">
              <button className="geupji-btn" onClick={() => setSpecModal(false)} disabled={busy}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {regModal && (
        <div className="geupji-modal-backdrop" onClick={() => !busy && setRegModal(false)}>
          <div className="geupji-modal wide" onClick={(e) => e.stopPropagation()}>
            <div className="geupji-modal-head">규제 관리</div>
            <div className="geupji-modal-body">
              <div className="geupji-add-row">
                <span className="geupji-add-region">{selectedRegionLabel}</span>
                <select value={regReg} onChange={(e) => setRegReg(e.target.value)}>
                  <option value="조정">조정</option>
                  <option value="비조정">비조정</option>
                </select>
                <input type="date" value={regDate} onChange={(e) => setRegDate(e.target.value)} />
                <button className="geupji-btn primary" onClick={addRegulation} disabled={busy}>추가</button>
              </div>
              <div className="geupji-manage-split">
                <div className="geupji-manage-left">
                  <DesignTree
                    rows={regList}
                    busy={busy}
                    editKey={editKey}
                    renderValue={(r) => `${r.regulation ?? ''}${r.regulation_date ? ` · ${String(r.regulation_date).slice(0, 10)}` : ''}`}
                    renderEdit={() => (
                      <span className="geupji-edit-inline">
                        <select value={editReg} onChange={(e) => setEditReg(e.target.value)}>
                          <option value="조정">조정</option>
                          <option value="비조정">비조정</option>
                        </select>
                        <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
                      </span>
                    )}
                    onEdit={(guCode, r) => { setEditKey(guCode); setEditReg(r.regulation || '조정'); setEditDate((r.regulation_date ?? '').toString().slice(0, 10)); }}
                    onSave={(guCode) => saveRegGroup(guCode)}
                    onCancel={() => setEditKey(null)}
                    onDelete={(guCode) => deleteRegGroup(guCode)}
                  />
                </div>
                <div className="geupji-manage-right">
                  <DesignSummary rows={regList} />
                </div>
              </div>
              <p className="geupji-modal-note">시도 &gt; 시군구(구 단위)로 표기. 추가·수정·삭제 모두 해당 시군구 하위 전체에 일괄 적용됩니다.</p>
            </div>
            <div className="geupji-modal-actions">
              <button className="geupji-btn" onClick={() => setRegModal(false)} disabled={busy}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MasterPane({ token, setError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setRows(await queryMaster(token)); }
    catch (e) { setError(e?.message || '급지마스터 조회 실패'); setRows([]); }
    finally { setLoading(false); }
  }, [token, setError]);

  useEffect(() => { load(); }, [load]);

  const baseDateStr = (v) => (v ? String(v).slice(0, 10) : '');

  const saveCell = async (rowIdx, field, newValue) => {
    const row = rows[rowIdx];
    if (!row) return;
    const oldPrice = Number(row.pyeong_price);
    const payload = {
      baseDate: baseDateStr(row.base_date),
      oldPyeongPrice: oldPrice,
      newPyeongPrice: field === 'pyeong_price' ? Number(String(newValue).replace(/,/g, '')) : Number(row.pyeong_price),
      geupji: field === 'geupji' ? parseInt(String(newValue).replace(/,/g, ''), 10) : (row.geupji != null ? Number(row.geupji) : null),
    };
    if (payload.newPyeongPrice == null || Number.isNaN(payload.newPyeongPrice) || payload.geupji == null || Number.isNaN(payload.geupji)) {
      setError('평당가/급지는 숫자여야 합니다.'); await load(); return;
    }
    const res = await updateMasterCell(token, payload);
    if (!res.ok) { setError(res.message || '저장 실패'); await load(); return; }
    await load();
  };

  return (
    <div className="geupji-pane">
      <div className="geupji-toolbar">
        <span className="geupji-hint">최신 기준표만 표시됩니다. 평당가/급지 셀을 편집하면 즉시 저장됩니다.</span>
        <span className="geupji-spacer" />
        <button className="geupji-btn" onClick={load} disabled={loading}>새로고침</button>
      </div>
      <div className="geupji-grid-wrap">
        <table className="geupji-grid master">
          <thead>
            <tr><th style={{ minWidth: 120 }}>기준일</th><th style={{ minWidth: 140 }}>평당가</th><th style={{ minWidth: 100 }}>급지</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td className="geupji-empty" colSpan={3}>{loading ? '조회 중...' : '데이터가 없습니다.'}</td></tr>
            )}
            {rows.map((row, rowIdx) => (
              <tr key={`${baseDateStr(row.base_date)}-${row.pyeong_price}`}>
                <td>{baseDateStr(row.base_date)}</td>
                <td className="editable">
                  <input
                    defaultValue={fmtCell(row.pyeong_price)}
                    onBlur={(e) => saveCell(rowIdx, 'pyeong_price', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </td>
                <td className="editable">
                  <input
                    defaultValue={fmtCell(row.geupji)}
                    onBlur={(e) => saveCell(rowIdx, 'geupji', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GeupjiManagement() {
  const { token } = useAuth();
  const [tab, setTab] = useState('region');
  const [sidoList, setSidoList] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try { setSidoList(await getSido(token)); }
      catch (e) { setError(e?.message || '시도 조회 실패'); }
    })();
  }, [token]);

  const paneProps = useMemo(() => ({ token, sidoList, setError }), [token, sidoList]);

  return (
    <div className="geupji-management">
      {!!error && <div className="admin-table-status error">{error}</div>}
      <div className="geupji-tabs">
        <button className={`geupji-tab ${tab === 'region' ? 'active' : ''}`} onClick={() => setTab('region')}>지역데이터</button>
        <button className={`geupji-tab ${tab === 'master' ? 'active' : ''}`} onClick={() => setTab('master')}>급지마스터</button>
      </div>
      {tab === 'region' ? <RegionDataPane {...paneProps} /> : <MasterPane token={token} setError={setError} />}
    </div>
  );
}

export default GeupjiManagement;
